import { Reducer } from 'redux'

import { AppState, AppActionTypes } from './types'
import { modelReducer } from 'store/model'
import { interfaceReducer } from 'store/interface'

const resetState: AppState = {
  info: undefined,
  hasUnsavedChanges: undefined,
  saving: false,
  model: {},
  showCreateAppDialog: false,
  appReloaded: undefined,
  hasProcessDelayed: undefined,
  hasActiveProcess: undefined,
  tasksBoard: undefined,
  interface: {
    tabs: [],
    list: [],
    reloadLayout: 0,
    editMode: false,
    tabPanelValue: 0
  },
  changingAppVersion: false,
  creatingAppVersion: false,
  appVersionsDialog: {
    show: false,
    folder: undefined,
    openAsReadOnly: undefined,
    openOnNewInstance: undefined
  },
  refreshScenariosTableCounter: 0,
  waiting: false
}
const initialState: AppState = {
  ...resetState,
  broadcastChannel: undefined
}

const reducer: Reducer<AppState> = (
  state: AppState = initialState,
  action: { type: AppActionTypes; payload?: any }
) => {
  switch (action.type) {
    case AppActionTypes.SET_OPEN_APP: {
      return {
        ...state,
        ...{ info: action.payload || undefined },
        // Opening an app answers this question itself; a leftover answer from
        // the app that was open before is about a different process list — and
        // so is a process the board was left on.
        hasActiveProcess: undefined,
        tasksBoard: undefined,
        model: { nodeErrors: [], interfaceErrors: [], nodeInterfaceErrors: [], nodeConsole: [] }
      }
    }
    case AppActionTypes.SET_HAS_ACTIVE_PROCESS: {
      return {
        ...state,
        hasActiveProcess: action.payload
      }
    }
    case AppActionTypes.SET_TASKS_BOARD_VIEW: {
      return {
        ...state,
        tasksBoard: { ...state.tasksBoard, ...action.payload }
      }
    }
    case AppActionTypes.RESET_APP_INFO: {
      return {
        ...state,
        ...resetState
      }
    }
    case AppActionTypes.GET_APP_CONSOLIDATION_DIFFERENCES: {
      return {
        ...state,
        appConsolidationDifferences: action.payload
      }
    }
    case AppActionTypes.SET_APP_HAS_UNSAVED_CHANGES: {
      return {
        ...state,
        hasUnsavedChanges: action.payload
      }
    }
    case AppActionTypes.APP_SAVED_FROM_OPEN_APP: {
      return {
        ...state,
        appSavedFromOpenApp: action.payload
      }
    }
    case AppActionTypes.SET_SAVING_APP: {
      return {
        ...state,
        saving: action.payload
      }
    }
    case AppActionTypes.CLOSING_APP: {
      return {
        ...state,
        closingApp: action.payload
      }
    }
    case AppActionTypes.OPEN_NOTIFICATIONS: {
      return {
        ...state,
        openNotifications: action.payload
      }
    }
    case AppActionTypes.SET_NOTIFICATIONS_BELL_MOUNTED: {
      return {
        ...state,
        notificationsBellMounted: action.payload
      }
    }
    case AppActionTypes.SHOW_UNREAD_NOTIFICATIONS: {
      return {
        ...state,
        showUnreadNotifications: action.payload
      }
    }
    case AppActionTypes.SHOW_CREATE_APP_DIALOG: {
      return {
        ...state,
        showCreateAppDialog: action.payload
      }
    }
    case AppActionTypes.SET_APP_INFO: {
      return {
        ...state,
        info: action.payload
      }
    }
    case AppActionTypes.UPDATE_DEFAULT_INTERFACE_ID: {
      return {
        ...state,
        info: { ...state.info, defaultInterfaceId: action.payload }
      }
    }
    case AppActionTypes.APP_RELOADED: {
      return {
        ...state,
        appReloaded: action.payload
      }
    }
    case AppActionTypes.SET_CHANGING_APP_VERSION: {
      return {
        ...state,
        changingAppVersion: action.payload
      }
    }
    case AppActionTypes.SET_CREATING_APP_VERSION: {
      return {
        ...state,
        creatingAppVersion: action.payload
      }
    }
    case AppActionTypes.HAS_PROCESS_DELAYED: {
      return {
        ...state,
        hasProcessDelayed: action.payload
      }
    }
    case AppActionTypes.SHOW_APP_VERSIONS_DIALOG: {
      return {
        ...state,
        appVersionsDialog: action.payload
      }
    }
    case AppActionTypes.REFRESH_SCENARIOS_TABLE: {
      return {
        ...state,
        refreshScenariosTableCounter: (state.refreshScenariosTableCounter || 0) + 1
      }
    }
    case AppActionTypes.SET_OPEN_APP_AFTER_SAVE: {
      return {
        ...state,
        openAppAfterSave: action.payload
      }
    }
    case AppActionTypes.ADD_BROADCAST_CHANNEL: {
      const channel = new BroadcastChannel(action.payload)
      if (state.broadcastChannel) {
        state.broadcastChannel.close()
      }
      return {
        ...state,
        broadcastChannel: channel
      }
    }
    case AppActionTypes.REMOVE_BROADCAST_CHANNEL: {
      if (state.broadcastChannel) {
        state.broadcastChannel.close()
      }
      return {
        ...state,
        broadcastChannel: undefined
      }
    }
    case AppActionTypes.SET_SAVING_APP_WITH_INTERFACE_EDIT_MODE: {
      return {
        ...state,
        savingAppWithInterfaceEditMode: action.payload
      }
    }
    case AppActionTypes.SET_OPEN_SAVE_APP_DIALOG_FROM_INTERFACE: {
      return {
        ...state,
        openSaveAppDialogFromInterface: action.payload
      }
    }
    case AppActionTypes.SET_WAITING: {
      return {
        ...state,
        waiting: action.payload
      }
    }
    case AppActionTypes.SET_ANALYSIS_RESULT: {
      let applicationAnalysisResult = state.applicationAnalysisResult
      return {
        ...state,
        applicationAnalysisResult: { ...applicationAnalysisResult, ...action.payload }
      }
    }
    case AppActionTypes.CLEAN_ANALYSIS_RESULT: {
      return {
        ...state,
        applicationAnalysisResult: undefined
      }
    }
    case AppActionTypes.SET_ASSETS_RECOVERY_DIALOG: {
      return {
        ...state,
        assetsRecoveryDialog: action.payload
      }
    }
    case AppActionTypes.SET_OPENING_APP_NAME: {
      return {
        ...state,
        openingAppName: action.payload
      }
    }
    case AppActionTypes.SET_APP_HAS_DOCUMENTATION: {
      return {
        ...state,
        info: { ...state.info, appHasDocumentation: action.payload }
      }
    }
    case AppActionTypes.SET_CURRENT_TEAM_TAB: {
      return {
        ...state,
        currentTeamTab: action.payload
      }
    }
    case AppActionTypes.SET_COMMON_TAB_NAME: {
      return {
        ...state,
        commonTabName: action.payload
      }
    }
    case AppActionTypes.SET_CURRENT_APP_FOLDER: {
      return {
        ...state,
        currentAppFolder: action.payload
      }
    }
    case (action.type.match('^@@model') || {}).input:
      return { ...state, model: modelReducer(state.model, action) }

    case (action.type.match('^@@interface') || {}).input:
      return { ...state, interface: interfaceReducer(state.interface, action) }
    case AppActionTypes.CANCELING_OPEN_APP: {
      return {
        ...state,
        cancelingOpenApp: action.payload
      }
    }

    default: {
      return state
    }
  }
}

export { reducer as appReducer }
